import React, { useState, useEffect } from "react";
import { Card, Button, message, Typography, Space, Select, Tag, Modal, Form, InputNumber, Input, Statistic, Row, Col, Popconfirm } from "antd";
import Alert from "../components/GxAlert";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import {
  getEmployees, getPeriods,
  getDriverRates, createDriverRate, updateDriverRate, toggleDriverRate, deleteDriverRate,
  getDriverEntries, createDriverEntry, deleteDriverEntry, getDriverSummary,
} from "../api/client";
import GxTable from "../components/GxTable";
import EditableTable from "../components/EditableTable";
import { filterByChildren } from "../utils/selectFilter";
import { faNum } from "../utils/faNumberFormat";
import { getActiveYm, pickActivePeriod, rememberPeriod, setActiveYm } from "../utils/activeMonth";

const { Title } = Typography;
const { Option } = Select;
const fa = (n) => { if (n === null || n === undefined || n === "") return "—"; return faNum(Number(n)); };

/* بیمه از این صفحه برداشته شد: بیمه بخشِ دومرحله‌ایِ خودش را دارد
   (کسر حق بیمه در فیش + پرداختیِ بیمه)، و کارتِ ایمپورتِ اینجا نسخهٔ
   موازی و گمراه‌کننده‌ای از همان بود. */
export default function InsuranceDriverPage({ embedded = false }) {
  const [employees, setEmployees] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [periodId, setPeriodId] = useState(null);
  const [rates, setRates] = useState([]);
  const [entries, setEntries] = useState([]);
  const [drvSummary, setDrvSummary] = useState(null);
  const [rateModal, setRateModal] = useState(false);
  const [entryModal, setEntryModal] = useState(false);
  const [rateForm] = Form.useForm();
  const [entryForm] = Form.useForm();

  useEffect(() => {
    getEmployees({ limit: 1000 }).then(r => setEmployees(Array.isArray(r.data) ? r.data : (r.data.items || [])));
    getPeriods().then(r => { setPeriods(r.data); const p = pickActivePeriod(r.data); if (p) setPeriodId(p.id); });
    getDriverRates().then(r => setRates(r.data));
  }, []);

  const loadDrv = () => { if (periodId) { getDriverEntries({ period_id: periodId }).then(r => setEntries(r.data)); getDriverSummary(periodId).then(r => setDrvSummary(r.data)); } };
  useEffect(() => { loadDrv(); /* eslint-disable-next-line */ }, [periodId]);

  /* فهرستِ این صفحه فقط **راننده‌ها** — خواستهٔ کاربر: «لیستی که مربوط به یک
     بخشِ خاص است، فقط همان نقش را بیاورد». اگر هیچ‌کس تیکِ راننده نخورده
     باشد، به کلِ پرسنل برمی‌گردد تا کاربر در بن‌بست نیفتد. */
  const driverOptions = React.useMemo(() => {
    const drv = (employees || []).filter((e) => e.is_driver);
    return drv.length ? drv : (employees || []);
  }, [employees]);
  const noDrivers = (employees || []).length > 0
    && !(employees || []).some((e) => e.is_driver);

  const periodLabel = (p) => `${p.year}/${String(p.month).padStart(2, "0")}`;
  const periodSelector = (
    <Select value={periodId} onChange={(v) => { setPeriodId(v); rememberPeriod(periods, v); }} style={{ width: 130 }} placeholder="دوره">
      {periods.map(p => <Option key={p.id} value={p.id}>{periodLabel(p)}</Option>)}
    </Select>);

  const submitRate = async (v) => {
    try { await createDriverRate(v); message.success("ثبت شد"); setRateModal(false); rateForm.resetFields();
      getDriverRates().then(r => setRates(r.data)); }
    catch { message.error("خطا"); }
  };
  const submitEntry = async (v) => {
    try { await createDriverEntry({ ...v, payroll_period_id: periodId }); message.success("ثبت شد");
      setEntryModal(false); entryForm.resetFields(); loadDrv(); }
    catch (e) { message.error(e?.response?.data?.detail || "خطا"); }
  };

  const reloadRates = () => getDriverRates().then((x) => setRates(x.data));
  /* ویرایشِ درجا — تا امروز اصلاً وجود نداشت (اندپوینتِ PATCH بود ولی فرانت
     صدایش نمی‌زد)، پس نرخِ اشتباه فقط با ساختنِ نرخِ تازه قابلِ اصلاح بود. */
  const rateCols = [
    { title: "مأموریت", dataIndex: "name", editType: "text",
      editRule: (v) => (!String(v || "").trim() ? "نام لازم است" : null) },
    { title: "نرخ (ریال)", dataIndex: "fee", render: fa, editType: "number",
      editRule: (v) => (v == null || Number(v) <= 0 ? "نرخ باید بزرگ‌تر از صفر باشد" : null) },
    { title: "وضعیت", dataIndex: "active", width: 120,
      render: (v, r) => (
        <Tag color={v ? "green" : "default"} style={{ cursor: "pointer" }}
          onClick={() => toggleDriverRate(r.id).then((res) => {
            message.success(res.data.message); reloadRates();
          })}>
          {v ? "فعال" : "غیرفعال"}
        </Tag>) },
    { title: "", width: 50, gxExport: false, render: (_, r) => (
      <Popconfirm title="حذف شود؟"
        description="اگر در ثبت‌ها استفاده شده باشد، به‌جای حذف غیرفعال می‌شود تا سابقه از بین نرود."
        okText="بله" cancelText="خیر"
        onConfirm={() => deleteDriverRate(r.id).then((res) => {
          message.success(res.data.message); reloadRates();
        })}>
        <Button size="small" type="text" danger icon={<DeleteOutlined />} /></Popconfirm>) },
  ];
  const entryCols = [
    { title: "راننده", dataIndex: "employee_name" },
    { title: "مأموریت", dataIndex: "task_name" },
    { title: "تعداد", dataIndex: "count", render: fa },
    { title: "نرخ", dataIndex: "amount_snapshot", render: fa },
    { title: "جمع", dataIndex: "total_amount", render: v => <b>{fa(v)}</b> },
    { title: "", width: 50, render: (_, r) => (
      <Popconfirm title="حذف؟" onConfirm={() => deleteDriverEntry(r.id).then(() => { message.success("حذف شد"); loadDrv(); })}>
        <Button size="small" type="text" danger icon={<DeleteOutlined />} /></Popconfirm>) },
  ];

  return (
    <div>
      {!embedded && <Title level={5} style={{ marginBottom: 16 }}>راننده‌ها و مأموریت‌ها</Title>}

      <Card title="نرخ مأموریت‌ها" style={{ marginBottom: 16 }}
        extra={<Button icon={<PlusOutlined />} onClick={() => setRateModal(true)}>نرخ جدید</Button>}>
        <EditableTable layoutId="driver-rates" dataSource={rates} columns={rateCols}
          rowKey="id" pagination={false}
          onSave={(id, fields) => updateDriverRate(id, fields).then(() => {
            message.success("ویرایش شد"); reloadRates();
          })} />
      </Card>

      <Card title="ثبت ماهانهٔ مأموریت"
        extra={<Space>{periodSelector}
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setEntryModal(true)}>ثبت مأموریت</Button></Space>}>
        {noDrivers && (
          <Alert type="warning" showIcon style={{ marginBottom: 12 }}
            message="هیچ پرسنلی تیکِ «راننده» ندارد"
            description="در «لیست پرسنل ← نوع همکاری» برای راننده‌ها تیکِ راننده را بزنید؛ آن‌وقت این فهرست فقط همان‌ها را نشان می‌دهد. فعلاً کلِ پرسنل نمایش داده می‌شود." />
        )}
        {drvSummary && (
          <Row gutter={16} style={{ marginBottom: 12 }}>
            <Col><Statistic title="جمع کل حقوق راننده (دوره)" value={fa(drvSummary.grand_total)} /></Col>
          </Row>
        )}
        <GxTable dataSource={entries} columns={entryCols} rowKey="id" size="small" pagination={false} />
      </Card>

      <Modal title="نرخ مأموریت جدید" open={rateModal} onCancel={() => setRateModal(false)}
        onOk={() => rateForm.submit()} okText="ثبت" cancelText="انصراف">
        <Form form={rateForm} layout="vertical" onFinish={submitRate} style={{ direction: "rtl" }}>
          <Form.Item name="name" label="نام مأموریت" rules={[{ required: true }]}>
            <Input placeholder="مثلاً بار شهریار / بار ساوه / حملِ بارِ کارگاه / تخلیهٔ بار" /></Form.Item>
          <Form.Item name="fee" label="نرخ هر بار (ریال)" rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: "100%" }} /></Form.Item>
        </Form>
      </Modal>

      <Modal title="ثبت مأموریت ماهانه" open={entryModal} onCancel={() => setEntryModal(false)}
        onOk={() => entryForm.submit()} okText="ثبت" cancelText="انصراف">
        <Form form={entryForm} layout="vertical" onFinish={submitEntry} style={{ direction: "rtl" }}>
          <Form.Item name="employee_id" label="راننده" rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="children"
              filterOption={filterByChildren}>
              {driverOptions.map(e => <Option key={e.id} value={e.id}>{e.full_name}{e.employee_code ? ` (${e.employee_code})` : ""}</Option>)}
            </Select></Form.Item>
          <Form.Item name="task_rate_id" label="نوع مأموریت" rules={[{ required: true }]}>
            <Select>{rates.filter(r => r.active).map(r => <Option key={r.id} value={r.id}>{r.name} — {fa(r.fee)}</Option>)}</Select>
          </Form.Item>
          <Form.Item name="count" label="تعداد" rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: "100%" }} /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
